import { useState, useEffect, useRef, useCallback } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { config } from '../config';
import { generateUserColor } from '../utils/collaborationColors';

// Refresh token 10 minutes before expiry (token is valid for 4 hours)
const TOKEN_REFRESH_MS = (4 * 60 - 10) * 60 * 1000; // 3h50m

/**
 * Hook to manage Yjs collaboration setup for a page.
 *
 * Fetches a short-lived auth token, then provides a stable providerFactory
 * for Lexical's CollaborationPlugin. Also exposes awareness for the presence bar.
 *
 * @param {string} pageShortId - The page's shortId
 * @param {object} options
 * @param {boolean} options.enabled - Whether collaboration should be active
 * @param {object} options.user - Current user object ({ _id, username, profilePicture })
 * @returns {{ ready: boolean, providerFactory: Function, providerRef: React.MutableRefObject, awareness: object|null }}
 */
export function useYjsProvider(pageShortId, { enabled = false, user } = {}) {
  const [ready, setReady] = useState(false);
  const [awareness, setAwareness] = useState(null);
  // Real WS connection state from the provider, so the UI can show an honest
  // "connected / reconnecting / offline" indicator instead of a hardcoded one.
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const tokenRef = useRef(null);
  const providerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  // Track the enabled state in a ref to avoid destroying provider on brief flickers
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Fetch Yjs auth token
  const fetchToken = useCallback(async () => {
    if (!pageShortId || !user?._id) return null;

    try {
      const res = await fetch(
        `${config.API_URI}/v1/yjs/token?pageId=${pageShortId}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        console.error('[useYjsProvider] Failed to fetch token:', res.status);
        return null;
      }
      const data = await res.json();
      return data.token;
    } catch (err) {
      console.error('[useYjsProvider] Error fetching token:', err);
      return null;
    }
  }, [pageShortId, user?._id]);

  // Initial token fetch + schedule refresh
  useEffect(() => {
    if (!enabled || !pageShortId || !user?._id) {
      setReady(false);
      return;
    }

    let cancelled = false;

    async function init() {
      const token = await fetchToken();
      if (cancelled || !token) return;

      tokenRef.current = token;
      setReady(true);

      // Schedule token refresh
      refreshTimerRef.current = setInterval(async () => {
        if (!enabledRef.current) return;
        const newToken = await fetchToken();
        if (newToken) {
          tokenRef.current = newToken;
          // Update the provider's params for the next reconnect. In y-websocket
          // v3, `provider.url` is a GETTER computed from `provider.params` on
          // every connection — the old code assigned to that getter (a no-op),
          // so the token never actually rotated and long sessions silently
          // failed to reconnect once the original token expired.
          if (providerRef.current?.params) {
            providerRef.current.params.token = newToken;
          }
        }
      }, TOKEN_REFRESH_MS);
    }

    init();

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [enabled, pageShortId, user?._id, fetchToken]);

  // Clean up provider ONLY when page changes or component unmounts
  // Do NOT clean up on `enabled` change — CollaborationPlugin manages connect/disconnect
  useEffect(() => {
    return () => {
      if (providerRef.current) {
        providerRef.current._zbCleanup?.();
        providerRef.current.destroy();
        providerRef.current = null;
        setAwareness(null);
        setConnectionStatus('connecting');
      }
    };
  }, [pageShortId]);

  // Stable providerFactory for CollaborationPlugin
  const providerFactory = useCallback(
    (id, yjsDocMap) => {
      // If there's an existing provider, destroy it first
      if (providerRef.current) {
        providerRef.current._zbCleanup?.();
        providerRef.current.destroy();
        providerRef.current = null;
      }

      const doc = new Y.Doc();
      yjsDocMap.set(id, doc);

      const wsUrl = config.API_URI.replace(/^http/, 'ws');
      const provider = new WebsocketProvider(wsUrl + '/yjs', id, doc, {
        connect: false,
        params: { token: tokenRef.current },
        disableBc: true, // Disable BroadcastChannel (not needed for our use case)
      });

      // Set user info on awareness
      if (user) {
        provider.awareness.setLocalStateField('user', {
          id: user._id,
          name: user.name || user.username,
          color: generateUserColor(user._id),
          picture: user.profilePicture || null,
        });
      }

      // Track real connection state. y-websocket v3 emits 'status' with
      // { status: 'connected' | 'connecting' | 'disconnected' }. This lets the
      // editor show an honest reconnecting/offline indicator (and the previous
      // hardcoded "connected" dot was misleading when the socket was down).
      const handleStatus = ({ status }) => setConnectionStatus(status);
      const handleClose = () => setConnectionStatus('disconnected');
      provider.on('status', handleStatus);
      provider.on('connection-close', handleClose);
      // Remember the listeners so we can detach them when this provider is torn down.
      provider._zbCleanup = () => {
        provider.off('status', handleStatus);
        provider.off('connection-close', handleClose);
      };

      providerRef.current = provider;
      setAwareness(provider.awareness);

      return provider;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageShortId, user?._id]
  );

  return { ready: ready && enabled, providerFactory, providerRef, awareness, connectionStatus };
}
